import { type fabric } from 'fabric';
import { type Constructor } from '../types';
import tools from '../tools';
import type { KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { hasLockBehaviors, isSimpleSelection } from './guards';

interface LockBehaviors {
  initializeLockBehaviors(locked: boolean): void;
  lock(): void;
  unlock(): void;
}

// lockable objects may optionally define methods that'll be called when the object is locked/unlocked
interface Lockable {
  lock?(): void;
  unlock?(): void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function LockBehaviors(Base: Constructor<fabric.Object & Lockable>): any {
  return class extends Base implements LockBehaviors {
    __hasLockBehaviors = true;
    serializedKeys = [
      // @ts-expect-error serializedKeys is declared on subclasses
      ...this.serializedKeys,
      'locked',
    ];

    // Avoid naming conflicts with frame-like objects
    lockSelected = false;
    // Some objects initialize in a different order. Ensure all objects init
    // lock behaviors through initializeLockBehaviors
    lockInitialized: boolean;
    locked: boolean;
    _previousCursor: string | undefined;

    initializeLockBehaviors(locked: boolean) {
      this.lockInitialized = true;
      this._setLocked(locked);

      // SimpleSelections can't really be locked, but should reflect when they
      // contain only locked objects
      this.locked = locked;

      this.on('moved', () => {
        // Frames can still move us, so remove controls again when moved
        if (this.locked) {
          this.hasControls = false;
        }
      });

      this.on('selected', () => {
        this.lockSelected = true;
      });

      this.on('deselected', () => {
        this.lockSelected = false;
      });

      this.on('mousedown:before', () => {
        // If an object locked, clear canvas._target so that _groupSelector is
        // created
        if (this.locked && this.canvas._target === this) {
          this.canvas._target = null;
        }
      });

      this.on('mouseup:before', () => {
        if (this.locked && this.canvas.hasGroupSelector) {
          // Don't allow selection if dragging inside a locked object
          this.selectable = false;
        } else {
          this.selectable = true;
        }
      });

      this.on('mouseup', () => {
        this.selectable = true;
      });
    }

    lock() {
      this.set('locked', true);
      super.lock?.();
    }

    unlock() {
      this.set('locked', false);
      super.unlock?.();
    }

    _setLocked(locked: boolean) {
      if (!this.lockInitialized) return;

      if (isSimpleSelection(this)) {
        this.forEach(object => {
          if (hasLockBehaviors(object)) {
            locked ? object.lock() : object.unlock();
          }
        });
        this.handleLockedObjectSelection();
      }
      this.lockMovementX = locked;
      this.lockMovementY = locked;
      this.lockScalingX = locked;
      this.lockScalingY = locked;
      this.lockRotation = locked;
      this.hasControls = !locked;
      // Which mouse event selects the object
      this.activeOn = locked ? 'up' : 'down';
      this.canvas?.requestRenderAll();

      if (locked && this.hoverCursor !== this._previousCursor) {
        this._previousCursor = this.hoverCursor;
      }
      this.hoverCursor = locked ? 'default' : this._previousCursor;
    }

    // Locked objects can't interact with connectors
    attachmentPoints() {
      return this.locked ? [] : super.attachmentPoints?.() ?? [];
    }

    tools(topLevel = true) {
      const superTools = (super.tools && super.tools(topLevel)) || [];

      if (this.locked) {
        const filterToolsRecursive = (toolList: any[]): any[] => {
          return toolList
            .map(item => {
              if (Array.isArray(item)) {
                return filterToolsRecursive(item);
              } else {
                return item.availableWhenLocked ? item : null;
              }
            })
            .filter(
              item =>
                item !== null && (Array.isArray(item) ? item.length > 0 : true)
            );
        };

        const filteredGroups = filterToolsRecursive(superTools);
        return [...filteredGroups, [tools.ToolUnlock]];
      }

      // Allow super to override whether to show lock tool. Tables, for example,
      // need to conditionally show lock tool when cells are selected or not
      const superShowLockTool = super.showLockTool?.() ?? true;

      if (this.textEditing || !superShowLockTool) {
        return superTools;
      }

      return [...superTools, [tools.ToolLock]];
    }

    // Checks if this or an object within an active selection is locked
    get isLocked() {
      return (
        (this.locked ||
          (isSimpleSelection(this) && this._objects?.some(o => o.locked))) ??
        false
      );
    }

    _render(ctx: CanvasRenderingContext2D): void {
      super._render(ctx);

      const ownPadding = this.ownPadding ?? 0;

      if (this.locked && this.lockSelected) {
        ctx.save();

        const width = this.getScaledWidth();
        const height = this.getScaledHeight();
        const zoom = this.canvas?.getZoom() || 1;
        const lineWidth = Math.max(1, 2 / zoom);
        const offset = Math.max(2, lineWidth + 1);
        const padding = lineWidth * zoom + offset * zoom - 3;

        // Stamps are drawn differently, so just change selection box color
        if (this.type === 'stamp') {
          this.borderColor = this.lockedBorderColor;
        } else {
          // Remove shape border color in favor of locked border rect we will
          // draw here
          this.borderColor = 'transparent';
          ctx.scale(1 / this.scaleX, 1 / this.scaleY);
          ctx.strokeStyle = this.lockedBorderColor;
          ctx.lineWidth = lineWidth;
          ctx.strokeRect(
            -width / 2 - offset,
            -height / 2 - offset,
            width + 2 * offset,
            height + 2 * offset
          );
          // Add a padding to activeSelection box to encompass locker border
          // We do this here so the padding updates on each render, instead of
          // at selection creation
          const activeObject = this.canvas.getActiveObject();
          if (activeObject?.type === 'activeSelection') {
            activeObject.padding = ownPadding + padding;
          }
        }
        ctx.restore();
      } else {
        this.borderColor = this.unlockedBorderColor;
        this.padding = ownPadding;
      }
    }

    _set(keyOrPath: KeyPath, value: any): fabric.Object {
      if (keyOrPath === 'locked') {
        this._setLocked(value);
      }

      super._set(keyOrPath, value);

      return this;
    }
  };
}

export default LockBehaviors;
